<template>
  <div class="pdd-container">
    <v-card :disabled="disabledPdd" flat>
      <v-card-text>
        <div class="btn-container my-2">
          <v-btn class="mx-1" fab light small color="primary" :disabled="zoom > 3"
            @click="() => (zoom < 3 ? (zoom += 0.2) : '')">
            <v-icon dark> mdi-magnify-plus-outline </v-icon>
          </v-btn>

          <v-btn class="mx-1" fab light small color="primary" :disabled="zoom < 0.3"
            @click="() => (zoom > 0.3 ? (zoom -= 0.2) : '')">
            <v-icon dark> mdi-magnify-minus-outline </v-icon>
          </v-btn>
        </div>
        <v-btn fab light small color="primary" class="btn-expand mx-1 my-2" @click="expand">
          <v-icon dark> mdi-arrow-expand </v-icon>
        </v-btn>
        <div class="zoomable" ref="container">
          <div class="zoom-content" :style="{ transform: `scale(${zoom})` }">
            <img ref="image" v-src-auth="`storage/plant/img/${plantId}`" style="max-width: none"
              @image-loaded="imageLoaded" />
            <v-menu
              v-for="(sensor, index) in positionedSensors"
              :key="`inline-${index}`"
              open-on-hover
              offset-y
              :close-delay="150"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  v-bind="attrs"
                  v-on="on"
                  label
                  class="ma-2"
                  :style="getSensorChipStyle(sensor)"
                  @mouseenter.native="onSensorHover(sensor)"
                >
                  {{ sensor.label }}
                </v-chip>
              </template>
              <v-card min-width="320">
                <v-card-text>
                  <div class="popup-title">{{ sensor.label }}</div>
                  <div v-if="sensorHoverLoading[sensor.sensorId]" class="py-2">
                    Loading...
                  </div>
                  <div v-else-if="!getHoverSections(sensor.sensorId).length" class="py-2">
                    No sensor data available
                  </div>
                  <div v-else>
                    <div
                      v-for="section in getHoverSections(sensor.sensorId)"
                      :key="section.messageType"
                      class="hover-section"
                    >
                      <div class="popup-subtitle">
                        {{ section.messageType }}
                      </div>
                      <v-simple-table dense>
                        <template v-slot:default>
                          <tbody>
                            <tr
                              v-for="row in section.rows"
                              :key="`${section.messageType}-${row.key}`"
                            >
                              <td>{{ row.key }}</td>
                              <td>{{ row.value }}</td>
                              <td>{{ row.unit }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-menu>
          </div>
        </div>
      </v-card-text>
    </v-card>
    <v-dialog v-model="dialog" fullscreen persistent>
      <v-card style="position: relative">
        <v-card-text>
          <div style="position: fixed" class="btn-container mx-4 my-2">
            <v-btn class="mx-1" fab light big color="primary" :disabled="zoom > 3"
              @click="() => (zoom < 3 ? (zoom += 0.2) : '')">
              <v-icon dark> mdi-magnify-plus-outline </v-icon>
            </v-btn>

            <v-btn class="mx-1" fab light big color="primary" :disabled="zoom < 0.3"
              @click="() => (zoom > 0.3 ? (zoom -= 0.2) : '')">
              <v-icon dark> mdi-magnify-minus-outline </v-icon>
            </v-btn>
          </div>
          <v-btn fab light big color="primary" class="btn-expand mx-5 my-2" style="position: fixed" @click="collapse">
            <v-icon dark> mdi-arrow-collapse </v-icon>
          </v-btn>
          <div class="zoomable dialog" ref="container-dialog">
            <div class="zoom-content" :style="{ transform: `scale(${zoom})` }">
              <img ref="image-dialog" v-src-auth="`storage/plant/img/${plantId}`" style="max-width: none"
                @image-loaded="imageLoadedDialog" />
              <v-menu
                v-for="(sensor, index) in positionedSensors"
                :key="`dialog-${index}`"
                open-on-hover
                offset-y
                :close-delay="150"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    v-bind="attrs"
                    v-on="on"
                    label
                    class="ma-2"
                    :style="getSensorChipStyle(sensor)"
                    @mouseenter.native="onSensorHover(sensor)"
                  >
                    {{ sensor.label }}
                  </v-chip>
                </template>
                <v-card min-width="320">
                  <v-card-text>
                    <div class="popup-title">{{ sensor.label }}</div>
                    <div v-if="sensorHoverLoading[sensor.sensorId]" class="py-2">
                      Loading...
                    </div>
                    <div v-else-if="!getHoverSections(sensor.sensorId).length" class="py-2">
                      No sensor data available
                    </div>
                    <div v-else>
                      <div
                        v-for="section in getHoverSections(sensor.sensorId)"
                        :key="section.messageType"
                        class="hover-section"
                      >
                        <div class="popup-subtitle">
                          {{ section.messageType }}
                        </div>
                        <v-simple-table dense>
                          <template v-slot:default>
                            <tbody>
                              <tr
                                v-for="row in section.rows"
                                :key="`${section.messageType}-${row.key}`"
                              >
                                <td>{{ row.key }}</td>
                                <td>{{ row.value }}</td>
                                <td>{{ row.unit }}</td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<style scoped>
.pdd-container {
  height: 100%;
}

.zoomable {
  position: relative;
  width: 100%;
  height: 520px;
  overflow: auto;
}

.zoomable.dialog {
  height: 100vh;
}

.zoom-content {
  position: relative;
  transition: transform 0.3s ease;
  transform-origin: top left;
  width: 100%;
  height: 100%;
}

.btn-container {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
}

.btn-expand {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
}

.popup-title {
  font-weight: 600;
}

.popup-subtitle {
  font-size: 0.85rem;
  font-weight: 600;
  margin: 8px 0 4px;
}

.hover-section + .hover-section {
  margin-top: 12px;
}
</style>

<script>
import { mapState } from "vuex";

export default {
  data() {
    return {
      zoom: 1,
      positionedSensors: [],
      dialog: false,
      imgLoaded: false,
      imgDialogLoaded: false,
      disabledPdd: false,
      sensorHoverData: {},
      sensorHoverLoading: {},
    };
  },
  computed: {
    ...mapState(["plantId"]),
  },
  created: function () {
    this.getData();
  },
  watch: {
    plantId: function () {
      this.getData();
    },
    dialog(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.onDialogEnter();
        });
      }
    },
  },
  methods: {
    getData: async function () {
      const response = await this.$store.dispatch(
        "_get2DInspectionData",
        this.plantId
      );
      this.plant = response.label;
      this.img = response.img;
      this.sensorHoverData = {};
      this.sensorHoverLoading = {};
      this.positionedSensors = response.sensors.filter((x) => x.posX && x.posY);
    },
    getSensorChipStyle(sensor) {
      return {
        backgroundColor: sensor.connected
          ? "rgba(50, 250, 50, 0.8)"
          : "rgba(250, 50, 50,0.8)",
        position: "absolute",
        left: `${sensor.posX}px`,
        top: `${sensor.posY}px`,
      };
    },
    async onSensorHover(sensor) {
      if (!sensor?.sensorId) {
        return;
      }

      if (
        this.sensorHoverLoading[sensor.sensorId] ||
        this.sensorHoverData[sensor.sensorId]
      ) {
        return;
      }

      this.$set(this.sensorHoverLoading, sensor.sensorId, true);

      try {
        const data = await this.$store.dispatch("_getPointHoverData", {
          pointId: sensor.sensorId,
        });

        this.$set(this.sensorHoverData, sensor.sensorId, data || []);
      } finally {
        this.$set(this.sensorHoverLoading, sensor.sensorId, false);
      }
    },
    getHoverSections(sensorId) {
      const records = this.sensorHoverData[sensorId] || [];

      return records.map((record) => ({
        messageType: record.messageType,
        rows: this.toTableRows(record),
      }));
    },
    toTableRows(record) {
      const rows = [];

      if (record.measureTime) {
        rows.push({
          key: "Measure Time",
          value: this.formatValue(record.measureTime, 'DATETIME'),
        });
      }

      return rows.concat(this.flattenPayload(record.payload));
    },
    flattenPayload(payload, prefix = "") {
      if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        return payload === null || payload === undefined
          ? []
          : [{ key: prefix || "value", value: this.formatValue(payload), unit: null }];
      }

      return Object.keys(payload)
        .sort()
        .flatMap((key) => {
          const nextPrefix = prefix ? `${prefix}.${key}` : key;
          const value = payload[key].value;
          const unit = payload[key].unit;

          if (value && typeof value === "object" && !Array.isArray(value)) {
            return this.flattenPayload(value, nextPrefix);
          }

          return [{ key: nextPrefix, value: this.formatValue(value), unit }];
        });
    },
    formatValue(value, type = null) {

      const CONF = this.$store.state.user.configParams;

      if (value === null || value === undefined || value === "") {
        return "-";
      }

      if (Array.isArray(value)) {
        return value.length ? value.join(", ") : "-";
      }

      if (typeof value === "object") {
        return JSON.stringify(value);
      }

      if (type === 'DATETIME') {
        return this.$date.fromISO(value).toFormat(CONF.dateTimeFormat);
      }

      return String(value);
    },
    expand() {
      this.dialog = true;
    },
    onDialogEnter() {
      if (this.imgDialogLoaded) {
        this.imageLoadedDialog();
      }
    },
    collapse() {
      this.dialog = false;
      if (this.imgLoaded) {
        this.imageLoaded();
      }
    },
    imageLoaded(evt) {
      const img = this.$refs.image;
      if (evt && evt.detail?.notFound) {
        this.disabledPdd = true;
      } else {
        const container = this.$refs.container;
        this.imgLoaded = true;
        this.adaptImageZoom(img, container);
      }
    },
    imageLoadedDialog() {
      const img = this.$refs["image-dialog"];
      const container = this.$refs["container-dialog"];
      this.imgDialogLoaded = true;
      this.adaptImageZoom(img, container);
    },
    adaptImageZoom(img, container) {
      if (!img || !container) {
        return;
      }
      // Calcolare lo zoom in base alle dimensioni dell'immagine e del contenitore
      const containerWidth = container.offsetWidth;
      const imageWidth = img.naturalWidth;

      if (imageWidth > containerWidth) {
        this.zoom = containerWidth / imageWidth;
      } else {
        this.zoom = 1; // Se l'immagine è più piccola, zoom al 100%
      }
    },
  },
};
</script>
